<template>
  <div>
    <Card pt:content:class="p-0!" pt:body:class="p-2!">
      <template #content>
        <div class="h-[72vh]">
          <ag-grid-vue
            theme="legacy"
            :rowData="projectReminders"
            :columnDefs="colDefs"
            :defaultColDef="defaultColDefs"
            :autoSizeStrategy="{ type: 'fitGridWidth' }"
            suppressCellFocus
            singleClickEdit
            stopEditingWhenCellsLoseFocus
            suppressDragLeaveHidesColumns
            class="h-full py-3"
            :class="darkVal ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'"
            :pinnedBottomRowData="[{}]"
            :isFullWidthRow="(params: IsFullWidthRowParams) => params.rowNode.rowPinned === 'bottom'"
            :fullWidthCellRenderer="CustomAddNewCellRenderer"
            :fullWidthCellRendererParams="{
              callback: () => addNewReminder(),
              label: 'reminder',
              display: canEdit,
            }"
            @grid-size-changed="resize"
            @grid-ready="(event: GridReadyEvent) => (gridApi = event.api)"
          ></ag-grid-vue>
        </div>
      </template>
    </Card>

    <TSDialog v-model:visible="mode" size="w-auto" :label="mode + ' Project Reminder'" :icon="mode === 'Add' ? 'plus-circle' : 'pencil'">
      <template #content>
        <ReminderOptions
          v-model:reminders="reminders"
          :project-id="projectId"
          :last-ordinal="projectReminders.length"
          :editing-project-reminder="editingProjectReminder"
          @added="projectReminderAdded"
          @edited="projectReminderEdited"
          @reminder-edited="reminderEdited"
        ></ReminderOptions>
      </template>
    </TSDialog>
  </div>
</template>

<script setup lang="ts">
import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { usePropsStore } from "@/stores/props";
import type { ProjectReminder, Reminder } from "@/types/Reminder";
import CustomAddNewCellRenderer from "../cell-renderers/CustomAddNewCellRenderer.vue";
import CustomBooleanIconCellRenderer from "@/components/cell-renderers/CustomBooleanIconCellRenderer.vue";
import CustomEditIconCellRenderer from "../cell-renderers/CustomEditIconCellRenderer.vue";
import CustomDeleteIconCellRenderer from "@/components/cell-renderers/CustomDeleteIconCellRenderer.vue";
import ReminderOptions from "../reminders/ReminderOptions.vue";
import { dateFormatter, getTableData, percentFormatter } from "@/services/Utility";
import type { GridApi, GridReadyEvent, IsFullWidthRowParams, ValueFormatterParams } from "ag-grid-community";

const projectReminders = defineModel<ProjectReminder[]>("projectReminders", { required: true });
const reminders = defineModel<Reminder[]>("reminders", { required: true });

//TODO: Remove 1900 date from database and also the -1's in other columns

interface Props {
  canEdit: boolean;
  projectId: string;
}
const props = defineProps<Props>();

const mode = ref<"Add" | "Edit">();

const editingProjectReminder = ref<ProjectReminder>();

const gridApi = ref<GridApi>();
const propsStore = usePropsStore();
const darkVal = computed<boolean>(() => propsStore.darkMode);

const addNewReminder = (): void => {
  mode.value = "Add";
};

const projectReminderAdded = (newProjectReminder: ProjectReminder): void => {
  gridApi.value?.applyTransaction({
    add: [newProjectReminder],
  });
  projectReminders.value = getTableData(gridApi.value);

  mode.value = undefined;
};

const projectReminderEdited = (editedProjectReminder: ProjectReminder): void => {
  const index = projectReminders.value.findIndex(
    (projectReminder: ProjectReminder) => projectReminder.projectReminderId === editedProjectReminder.projectReminderId
  );
  const rowNode = gridApi.value?.getDisplayedRowAtIndex(index);
  if (rowNode == null) return;

  rowNode.updateData(editedProjectReminder);
  projectReminders.value = getTableData(gridApi.value);
  editingProjectReminder.value = undefined;
  mode.value = undefined;
};

const reminderEdited = (reminder: Reminder): void => {
  projectReminders.value.forEach((projectReminder: ProjectReminder) => {
    if (projectReminder.reminder.reminderId === reminder.reminderId) {
      projectReminder.reminder = reminder;
    }
  });
};

const startEditingProjectReminder = (projectReminder: ProjectReminder): void => {
  editingProjectReminder.value = projectReminder;
  mode.value = "Edit";
};

const deleteProjectReminder = (projectReminder: ProjectReminder): void => {
  axios
    .post(`/project-reminder/delete/${projectReminder.projectReminderId}`)
    .then((response) => {
      handleApiResponse(
        response,
        (result: string) => {
          gridApi.value?.applyTransaction({
            remove: [projectReminder],
          });
          projectReminders.value = getTableData(gridApi.value);
        },
        (errors: ApiError[]) => {
          error("Cannot save budget", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const resize = (): void => {
  gridApi.value?.sizeColumnsToFit();
};

const defaultColDefs = {
  floatingFilter: false,
  enableCellChangeFlash: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  cellDataType: "text",
  sortable: false,
  headerClass: "grid-align-center",
  cellClass: "text-center",
};

const colDefs = ref([
  {
    field: "enabled",
    headerName: "Enabled",
    cellDataType: "boolean",
    cellClass: "text-center",
    minWidth: 75,
    width: 75,
    cellRenderer: CustomBooleanIconCellRenderer,
  },
  {
    field: "privateReminder",
    headerName: "Private",
    cellDataType: "boolean",
    cellClass: "text-center",
    minWidth: 75,
    width: 75,
    cellRenderer: CustomBooleanIconCellRenderer,
  },
  {
    field: "reminder.reminderTitle",
    headerName: "Title",
    minWidth: 150,
    width: 200,
  },
  {
    field: "reminder.reminderText",
    headerName: "Text",
    minWidth: 150,
    width: 400,
  },
  {
    field: "drawNumber",
    headerName: "Draw",
    valueFormatter: (params: ValueFormatterParams) => {
      if (params.value == null || params.value === -1) return "";
      return params.value;
    },
    minWidth: 75,
    width: 100,
  },
  {
    field: "percentCompleteSite",
    headerName: "Site % Complete",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
    minWidth: 75,
    width: 100,
  },
  {
    field: "percentDrawnBudget",
    headerName: "Budget % Drawn",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
    minWidth: 75,
    width: 100,
  },
  {
    field: "percentCompleteLineItem",
    headerName: "Line Item %",
    valueFormatter: (params: ValueFormatterParams) => percentFormatter(params, true),
    minWidth: 75,
    width: 100,
  },
  {
    field: "lineItem",
    headerName: "Line Item",
    minWidth: 75,
    width: 100,
  },
  {
    field: "reminderDate",
    headerName: "Date",
    cellDataType: "date",
    valueFormatter: (params: ValueFormatterParams) => dateFormatter(params),
    minWidth: 100,
    width: 100,
  },
  {
    field: "completedDate",
    headerName: "Completed Date",
    cellDataType: "date",
    valueFormatter: (params: ValueFormatterParams) => dateFormatter(params),
    minWidth: 75,
    width: 100,
  },
  {
    field: "edit",
    headerName: "",
    cellClass: "flex items-center justify-center",
    cellRenderer: CustomEditIconCellRenderer,
    cellRendererParams: {
      callback: startEditingProjectReminder,
      checkEditable: (projectReminder: ProjectReminder) => props.canEdit,
    },
    width: 50,
    minWidth: 50,
    hide: !props.canEdit,
  },
  {
    field: "delete",
    headerName: "",
    cellClass: "flex items-center justify-center",
    cellRenderer: CustomDeleteIconCellRenderer,
    cellRendererParams: {
      deleteConfirmMessage: (projectReminder: ProjectReminder) =>
        `Are you sure you want to delete this project reminder "${projectReminder.reminder.reminderTitle}"?`,
      deleteConfirmHeader: () => "Delete Project Reminder",
      callback: deleteProjectReminder,
      checkDeletable: (projectReminder: ProjectReminder) => props.canEdit,
    },
    width: 50,
    minWidth: 50,
    hide: !props.canEdit,
  },
]);
</script>
